/*
 * Product sliders: "Complete the look" on the product page and
 * "You might also like" in the cart.
 *
 * - <complementary-slider>: prev/next arrows scroll the track one card at a
 *   time; arrows dim at either end and hide when everything fits. Cards are
 *   often injected via innerHTML (where inline scripts don't run), so the
 *   element lives here and upgrades as soon as it's inserted.
 * - Adjustable rings: a popup asks for the ring size.
 * - "Choose size" on a card opens the theme's options picker.
 *
 * Loaded by the product page and the cart, sometimes both on one page, so it
 * only sets itself up once.
 */
(function () {
  if (!window.customElements || window.customElements.get('complementary-slider')) return;

  class ComplementarySlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cs-track]');
      this.prev = this.querySelector('[data-cs-prev]');
      this.next = this.querySelector('[data-cs-next]');
      if (!this.track) return;

      this.update = this.update.bind(this);
      this.onPrev = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);

      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.update, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.track) this.track.removeEventListener('scroll', this.update);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
    }

    step() {
      var card = this.track.querySelector('.cs-card:not([hidden])');
      if (!card) return this.track.clientWidth;
      var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    scrollByCard(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
    }

    update() {
      if (!this.track) return;
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var left = this.track.scrollLeft;
      this.classList.toggle('is-static', maxScroll <= 1);
      if (this.prev) this.prev.disabled = left <= 1;
      if (this.next) this.next.disabled = left >= maxScroll - 1;
    }
  }

  window.customElements.define('complementary-slider', ComplementarySlider);

  // The product page's required "Enter your Ring Size" box (Textify app)
  // makes the theme refuse every add to cart while it is empty. That box
  // only belongs to the main product, so adds from these cards, the quick-add
  // popups and the cart are let through. The theme checks the box before
  // doing anything else, so the required flag is lifted just for that check.
  var BYPASS_REQUIRED = '.cs-card, [data-quick-add-modal], [data-cart-drawer], [data-cart-page]';

  function patchAddToCart() {
    var cart = window.cart;
    if (!cart || typeof cart.addToCart !== 'function' || cart.addToCart.csPatched) return;
    var original = cart.addToCart;
    var patched = function (formData, button) {
      var bypass = button && button.closest && button.closest(BYPASS_REQUIRED);
      if (!bypass) return original.apply(this, arguments);
      var fields = Array.prototype.slice.call(document.querySelectorAll('#textfieldapp [required]'));
      fields.forEach(function (field) { field.removeAttribute('required'); });
      try {
        return original.apply(this, arguments);
      } finally {
        fields.forEach(function (field) { field.setAttribute('required', ''); });
      }
    };
    patched.csPatched = true;
    cart.addToCart = patched;
  }

  patchAddToCart();
  if (!window.cart) document.addEventListener('DOMContentLoaded', patchAddToCart);

  // Adjustable rings: a popup asks for the ring size, then adds the ring with
  // it saved as a line item property (same name as on the product page, so
  // orders match). Posts to /cart/add.js directly rather than through the
  // theme's add flow.
  var sizeDialog = null;
  var sizeVariantId = null;

  function closeSizeDialog() {
    if (!sizeDialog) return;
    if (typeof sizeDialog.close === 'function' && sizeDialog.open) sizeDialog.close();
    else sizeDialog.removeAttribute('open');
  }

  function openSizeDialog(card) {
    var scope = card.closest('.product__complementary, [data-cs-scope]');
    sizeDialog = (scope && scope.querySelector('[data-cs-size-dialog]')) || document.querySelector('[data-cs-size-dialog]');
    if (!sizeDialog) return;
    sizeVariantId = card.getAttribute('data-variant-id');

    sizeDialog.querySelector('[data-cs-size-title]').textContent = card.getAttribute('data-title') || '';
    sizeDialog.querySelector('[data-cs-size-price]').textContent = card.getAttribute('data-price') || '';
    var img = sizeDialog.querySelector('[data-cs-size-image]');
    var src = card.getAttribute('data-image');
    if (src) { img.src = src; img.hidden = false; } else { img.hidden = true; }
    var input = sizeDialog.querySelector('[data-cs-size-input]');
    input.value = '';
    input.classList.remove('is-invalid');
    sizeDialog.querySelector('[data-cs-size-error]').hidden = true;

    if (typeof sizeDialog.showModal === 'function') sizeDialog.showModal();
    else sizeDialog.setAttribute('open', '');
    input.focus();
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-cs-ring-size]');
    if (trigger) {
      var card = trigger.closest('[data-cs-adjustable]');
      if (card) {
        event.preventDefault();
        openSizeDialog(card);
      }
      return;
    }
    if (event.target.closest && event.target.closest('[data-cs-size-close]')) {
      closeSizeDialog();
      return;
    }
    // A click on the dimmed backdrop (the dialog element itself) closes it.
    if (sizeDialog && event.target === sizeDialog) closeSizeDialog();
  });

  // Escape closes the popup; don't let it also close the cart drawer behind
  // (the drawer closes on Escape keyup, which lands after the popup has
  // closed and handed focus back).
  var escapeClosedPopup = false;
  document.addEventListener('keydown', function (event) {
    if (event.code !== 'Escape') return;
    escapeClosedPopup = Boolean(sizeDialog && sizeDialog.open);
  }, true);
  document.addEventListener('keyup', function (event) {
    if (event.code !== 'Escape' || !escapeClosedPopup) return;
    escapeClosedPopup = false;
    event.stopPropagation();
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-cs-size-form]');
    if (!form) return;
    event.preventDefault();

    var dialog = form.closest('[data-cs-size-dialog]');
    var input = form.querySelector('[data-cs-size-input]');
    var error = form.querySelector('[data-cs-size-error]');
    var button = form.querySelector('[data-cs-size-submit]');
    var size = input.value.trim();

    if (!size) {
      input.classList.add('is-invalid');
      error.textContent = 'Please enter your ring size.';
      error.hidden = false;
      input.focus();
      return;
    }
    if (button.classList.contains('is-loading') || !sizeVariantId) return;
    button.classList.add('is-loading');
    error.hidden = true;

    var properties = {};
    properties[dialog.getAttribute('data-property') || 'Enter your Ring Size'] = size;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(sizeVariantId), quantity: 1, properties: properties }] })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function () {
        closeSizeDialog();
        if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
          if (typeof window.cart.openCartDrawer === 'function') window.cart.openCartDrawer();
        } else {
          window.location.href = root + 'cart';
        }
      })
      .catch(function (err) {
        error.textContent = (err && (err.description || err.message)) || 'Sorry, this ring could not be added. Please try again.';
        error.hidden = false;
      })
      .then(function () {
        button.classList.remove('is-loading');
      });
  });

  // "Choose size" on a card opens the same options picker as the card's bag
  // button.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-cs-choose]');
    if (!trigger) return;
    var holder = trigger.closest('[data-quick-add-holder]');
    var picker = holder && holder.querySelector('[data-quick-add-modal-handle]');
    if (picker && !picker.disabled) {
      event.preventDefault();
      picker.click();
    }
  });
})();
